import React from 'react';

/**
 * Lightweight markdown-to-JSX renderer for chat messages.
 * Handles: ### headers, **bold**, *italic*, `code`, bullet lists, numbered lists
 * (including GPT's "all 1." pattern), and line breaks.
 */

/**
 * Parse inline markdown (**bold**, *italic*, `code`) within a single line.
 */
function parseInline(text, keyPrefix = '') {
  if (!text) return [text];

  const parts = [];
  let idx = 0;

  // Regex matches: **bold**, *italic*, `code`
  const inlineRegex = /(\*\*(.+?)\*\*|\*(.+?)\*|`([^`]+?)`)/g;
  let match;
  let lastEnd = 0;

  while ((match = inlineRegex.exec(text)) !== null) {
    if (match.index > lastEnd) {
      parts.push(text.slice(lastEnd, match.index));
    }

    if (match[2]) {
      parts.push(<strong key={`${keyPrefix}-b-${idx++}`}>{match[2]}</strong>);
    } else if (match[3]) {
      parts.push(<em key={`${keyPrefix}-i-${idx++}`}>{match[3]}</em>);
    } else if (match[4]) {
      parts.push(
        <code
          key={`${keyPrefix}-c-${idx++}`}
          style={{
            background: 'rgba(87,59,118,0.08)',
            padding: '1px 5px',
            borderRadius: 4,
            fontSize: '0.92em',
            fontFamily: 'monospace',
          }}
        >
          {match[4]}
        </code>
      );
    }
    lastEnd = match.index + match[0].length;
  }

  if (lastEnd < text.length) {
    parts.push(text.slice(lastEnd));
  }

  return parts.length === 0 ? [text] : parts;
}

/**
 * Render a markdown string into React elements.
 *
 * Handles the common GPT pattern where numbered items have sub-bullets:
 *   1. **Name**
 *      - Email: ...
 *      - Detail: ...
 *   2. **Name**
 *      ...
 */
export default function renderMarkdown(text) {
  if (!text) return null;

  const lines = text.split('\n');
  const elements = [];
  let key = 0;

  // Accumulate numbered blocks: { title: string, subItems: string[] }
  let numberedBlocks = [];
  let currentBlock = null; // { title, subItems }
  let bulletItems = []; // standalone bullet list (not under a number)

  const flushBullets = () => {
    if (bulletItems.length > 0) {
      elements.push(
        <ul key={`ul-${key++}`} style={{ margin: '4px 0', paddingLeft: 20 }}>
          {bulletItems.map((item, i) => (
            <li key={`bli-${i}`}>{parseInline(item, `bli-${key}-${i}`)}</li>
          ))}
        </ul>
      );
      bulletItems = [];
    }
  };

  const flushNumbered = () => {
    if (numberedBlocks.length > 0) {
      elements.push(
        <ol key={`ol-${key++}`} style={{ margin: '6px 0', paddingLeft: 20 }}>
          {numberedBlocks.map((block, i) => (
            <li key={`nli-${i}`} style={{ marginBottom: 6 }}>
              <div>{parseInline(block.title, `nt-${key}-${i}`)}</div>
              {block.subItems.length > 0 && (
                <ul style={{ margin: '2px 0 0 0', paddingLeft: 16, listStyle: 'disc' }}>
                  {block.subItems.map((sub, j) => (
                    <li key={`nsi-${j}`} style={{ marginBottom: 1 }}>
                      {parseInline(sub, `ns-${key}-${i}-${j}`)}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ol>
      );
      numberedBlocks = [];
      currentBlock = null;
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // Ordered list item: 1. item, 2) item, etc.
    const olMatch = trimmed.match(/^(\d+)[.)]\s+(.+)/);
    // Unordered list item: - item or * item (but not ** bold) or • item
    const ulMatch = trimmed.match(/^[-•]\s+(.+)/) || trimmed.match(/^\*\s+([^*].*)$/);

    if (olMatch) {
      // New numbered item — flush any standalone bullets first
      flushBullets();

      // If there was a previous numbered block, save it
      if (currentBlock) {
        numberedBlocks.push(currentBlock);
      }
      currentBlock = { title: olMatch[2], subItems: [] };
    } else if (ulMatch) {
      if (currentBlock) {
        // Sub-bullet under a numbered item
        currentBlock.subItems.push(ulMatch[1]);
      } else {
        // Standalone bullet (not under a number)
        flushNumbered();
        bulletItems.push(ulMatch[1]);
      }
    } else if (trimmed === '') {
      // Blank line — if inside a numbered block, it's just spacing between items
      // Don't flush yet; the next numbered item will continue the list
      if (!currentBlock && numberedBlocks.length === 0) {
        flushBullets();
        elements.push(<div key={`br-${key++}`} style={{ height: 6 }} />);
      }
    } else {
      // ATX header: #, ##, ###, ####, #####, ######
      const headerMatch = trimmed.match(/^(#{1,6})\s+(.+)$/);
      if (headerMatch) {
        if (currentBlock) {
          numberedBlocks.push(currentBlock);
          currentBlock = null;
        }
        flushNumbered();
        flushBullets();

        const level = headerMatch[1].length;
        const headerText = headerMatch[2];
        const Tag = `h${Math.min(level, 6)}`;
        elements.push(
          <Tag
            key={`h-${key++}`}
            style={{
              margin: '12px 0 6px 0',
              fontSize: level <= 2 ? '1.1em' : '1em',
              fontWeight: 600,
              lineHeight: 1.3,
            }}
          >
            {parseInline(headerText, `h-${key}`)}
          </Tag>
        );
      } else {
        // Regular text line — flush any accumulated lists
        if (currentBlock) {
          numberedBlocks.push(currentBlock);
          currentBlock = null;
        }
        flushNumbered();
        flushBullets();

        elements.push(
          <div key={`p-${key++}`}>{parseInline(trimmed, `p-${key}`)}</div>
        );
      }
    }
  }

  // Flush remaining
  if (currentBlock) {
    numberedBlocks.push(currentBlock);
    currentBlock = null;
  }
  flushNumbered();
  flushBullets();

  return <>{elements}</>;
}
